import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { Send2 } from "iconsax-react";
import cn from "classnames";
import {
  type CreateCandidateFormValues,
  type CreateCandidateDto,
  createCandidateSchema,
  useHiringCreateCandidateMutation,
} from "@/features/hr/hiring";
import { Button, Input, ModalForm, PhoneInput } from "@/shared/ui";
import { useScrollDetection } from "@/shared/hooks";

interface Props {
  onClose: () => void;
  onSuccess: () => void;
  hidden?: boolean;
}

export default function CreateInvitationForm({ onClose, onSuccess, hidden = false }: Props) {
  const { t } = useTranslation("HiringPage");
  const { scrollRef, hasScroll } = useScrollDetection();

  const [createCandidate, { isLoading }] = useHiringCreateCandidateMutation();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    clearErrors,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateCandidateFormValues>({
    resolver: zodResolver(createCandidateSchema),
  });

  function handleTransientClose() {
    clearErrors();
    setServerError(null);
    onClose();
  }

  async function onSubmit(data: CreateCandidateFormValues) {
    if (isLoading) return;

    try {
      setServerError(null);
      const payload: CreateCandidateDto = {
        candidate_name: data.candidate_name,
        email: data.email,
        phone: data.phone,
        job_position: data.job_position || data.job_position_ru,
        job_position_ru: data.job_position_ru,
        job_position_kk: data.job_position_kk || data.job_position_ru,
        ...(data.intended_role_id && { intended_role_id: data.intended_role_id }),
      };
      await createCandidate(payload).unwrap();
      reset();
      clearErrors();
      setServerError(null);
      scrollRef.current?.scrollTo(0, 0);
      onSuccess();
    } catch (err) {
      setServerError(t("invite.error"));
      console.error("Ошибка при создании кандидата", err);
    }
  }

  return (
    <ModalForm
      icon={Send2}
      onClose={handleTransientClose}
      hidden={hidden}
      footer={
        <div className="grid grid-cols-[2fr_3fr] gap-3">
          <Button variant="secondary" size="huge" onClick={handleTransientClose} type="button">
            {t("invite.cancel")}
          </Button>
          <Button form="create-invitation-form" type="submit" variant="primary" size="huge" disabled={isLoading}>
            {isLoading ? t("invite.loading") : t("invite.send")}
          </Button>
        </div>
      }
    >
      <form id="create-invitation-form" className="flex flex-col h-full" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-2 shrink-0 pb-5 border-b surface-base-stroke">
          <h4 className="text-display-2xs content-base-primary">{t("invite.title")}</h4>
          <p className="text-body-regular-sm content-base-secondary">{t("invite.subtitle")}</p>
        </div>

        <div ref={scrollRef} className={cn("flex-1 overflow-auto flex flex-col gap-3 p-1 my-5 page-scroll", hasScroll && "pr-3")}>
            <Input
              label={t("invite.name")}
              hasRequiredLabel
              placeholder={t("invite.namePlaceholder")}
              {...register("candidate_name")}
              error={errors.candidate_name?.message ? t(errors.candidate_name.message as string) : undefined}
            />

            <Input
              label={t("invite.email")}
              hasRequiredLabel
              placeholder={t("invite.emailPlaceholder")}
              {...register("email")}
              error={errors.email?.message ? t(errors.email.message as string) : undefined}
            />

            <PhoneInput
              label={t("invite.phone")}
              hasRequiredLabel
              value={watch("phone")}
              onChange={(value) => setValue("phone", value, { shouldDirty: true })}
              error={errors.phone?.message ? t(errors.phone.message as string) : undefined}
            />

            <Input
              label={t("invite.position")}
              hasRequiredLabel
              placeholder={t("invite.positionPlaceholder")}
              {...register("job_position_ru")}
              error={errors.job_position_ru?.message ? t(errors.job_position_ru.message as string) : undefined}
            />

            {serverError && <p className="text-red-500">{serverError}</p>}
        </div>

      </form>
    </ModalForm>
  );
}
